import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, Pencil, Trash2, Upload, X, LogOut, Image } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";

type Project = {
  id: string;
  slug: string;
  title: string;
  category: string;
  thumbnail_url: string | null;
  description: string | null;
  overview: string | null;
  location: string | null;
  area: string | null;
  year: string | null;
};

type ProjectImage = {
  id: string;
  src_url: string;
  alt: string | null;
  section: string;
  sort_order: number;
};

const categories = ["Residential", "Commercial", "Interior", "Institutional", "Industrial", "Cultural", "Renovation", "Unique"];
const imageSections = ["exterior", "interior", "walkthrough"] as const;
const sectionLabels: Record<string, string> = { exterior: "Exterior", interior: "Interior", walkthrough: "Walkthrough" };

const ProjectsAdmin = () => {
  const { toast } = useToast();
  const { signOut, user } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Project | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: "", slug: "", category: "Residential", description: "", overview: "", location: "", area: "", year: "",
  });
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [sectionFiles, setSectionFiles] = useState<Record<string, File[]>>({ exterior: [], interior: [], walkthrough: [] });
  const [existingImages, setExistingImages] = useState<ProjectImage[]>([]);
  const [deletedImageIds, setDeletedImageIds] = useState<string[]>([]);

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["admin-projects"],
    queryFn: async () => {
      const { data, error } = await supabase.from("projects").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data as Project[];
    },
  });

  const generateSlug = (title: string) =>
    title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const uploadImage = async (file: File, folder: string): Promise<string> => {
    const ext = file.name.split(".").pop();
    const path = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from("project-images").upload(path, file);
    if (error) throw error;
    const { data } = supabase.storage.from("project-images").getPublicUrl(path);
    return data.publicUrl;
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      let thumbnailUrl = editing?.thumbnail_url || null;
      const slug = form.slug || generateSlug(form.title);

      if (thumbnailFile) {
        thumbnailUrl = await uploadImage(thumbnailFile, "thumbnails");
      }

      const payload = {
        title: form.title.trim(),
        slug,
        category: form.category,
        description: form.description.trim() || null,
        overview: form.overview.trim() || null,
        location: form.location.trim() || null,
        area: form.area.trim() || null,
        year: form.year.trim() || null,
        thumbnail_url: thumbnailUrl,
      };

      let projectId: string;

      if (editing) {
        const { error } = await supabase.from("projects").update(payload).eq("id", editing.id);
        if (error) throw error;
        projectId = editing.id;
      } else {
        const { data, error } = await supabase.from("projects").insert(payload).select("id").single();
        if (error) throw error;
        projectId = data.id;
      }

      // Delete removed images
      if (deletedImageIds.length > 0) {
        const { error } = await supabase.from("project_images").delete().in("id", deletedImageIds);
        if (error) throw error;
      }

      // Upload new section images
      for (const section of imageSections) {
        const files = sectionFiles[section];
        if (files.length === 0) continue;

        const existingCount = existingImages.filter(
          (img) => img.section === section && !deletedImageIds.includes(img.id)
        ).length;

        for (let i = 0; i < files.length; i++) {
          const url = await uploadImage(files[i], `${slug}/${section}`);
          const { error } = await supabase.from("project_images").insert({
            project_id: projectId,
            section,
            src_url: url,
            alt: files[i].name.replace(/\.[^.]+$/, ""),
            sort_order: existingCount + i,
          });
          if (error) throw error;
        }
      }
    },
    onSuccess: () => {
      toast({ title: editing ? "Project updated" : "Project created" });
      queryClient.invalidateQueries({ queryKey: ["admin-projects"] });
      resetForm();
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // Delete images first, then project
      await supabase.from("project_images").delete().eq("project_id", id);
      await supabase.from("project_videos").delete().eq("project_id", id);
      const { error } = await supabase.from("projects").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Project deleted" });
      queryClient.invalidateQueries({ queryKey: ["admin-projects"] });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const resetForm = () => {
    setEditing(null);
    setCreating(false);
    setThumbnailFile(null);
    setSectionFiles({ exterior: [], interior: [], walkthrough: [] });
    setExistingImages([]);
    setDeletedImageIds([]);
    setForm({ title: "", slug: "", category: "Residential", description: "", overview: "", location: "", area: "", year: "" });
  };

  const startEdit = async (p: Project) => {
    setEditing(p);
    setCreating(true);
    setForm({
      title: p.title, slug: p.slug, category: p.category,
      description: p.description || "", overview: p.overview || "",
      location: p.location || "", area: p.area || "", year: p.year || "",
    });

    // Fetch existing images for this project
    const { data } = await supabase
      .from("project_images")
      .select("*")
      .eq("project_id", p.id)
      .order("sort_order");
    if (data) setExistingImages(data as ProjectImage[]);
  };

  const addSectionFiles = (section: string, files: FileList | null) => {
    if (!files) return;
    setSectionFiles((prev) => ({
      ...prev,
      [section]: [...prev[section], ...Array.from(files)],
    }));
  };

  const removeSectionFile = (section: string, index: number) => {
    setSectionFiles((prev) => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index),
    }));
  };

  const removeExistingImage = (id: string) => {
    setDeletedImageIds((prev) => [...prev, id]);
  };

  const showForm = creating;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-muted-foreground hover:text-foreground"><ArrowLeft className="w-5 h-5" /></Link>
            <h1 className="text-lg font-bold text-foreground">Manage Projects</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground hidden sm:inline">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={signOut}><LogOut className="w-4 h-4 mr-1" /> Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {!showForm && (
          <>
            <div className="flex justify-between items-center mb-6">
              <p className="text-muted-foreground">{projects.length} projects</p>
              <Button onClick={() => setCreating(true)}><Plus className="w-4 h-4 mr-1" /> Add Project</Button>
            </div>

            {isLoading ? (
              <div className="text-center py-12 text-muted-foreground">Loading…</div>
            ) : (
              <div className="space-y-3">
                {projects.map((p) => (
                  <motion.div
                    key={p.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center gap-4 bg-card border border-border rounded-lg p-4"
                  >
                    {p.thumbnail_url && (
                      <img src={p.thumbnail_url} alt={p.title} className="w-16 h-12 object-cover rounded" />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground truncate">{p.title}</h3>
                      <p className="text-xs text-muted-foreground">{p.category} · /{p.slug}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={() => startEdit(p)}><Pencil className="w-4 h-4" /></Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          if (confirm("Delete this project?")) deleteMutation.mutate(p.id);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}

        {showForm && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-foreground">{editing ? "Edit Project" : "New Project"}</h2>
              <Button variant="ghost" size="icon" onClick={resetForm}><X className="w-5 h-5" /></Button>
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(); }}
              className="space-y-4 max-w-2xl"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: generateSlug(e.target.value) })} required maxLength={200} />
                </div>
                <div className="space-y-2">
                  <Label>Slug</Label>
                  <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} maxLength={200} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} maxLength={200} />
                </div>
                <div className="space-y-2">
                  <Label>Year</Label>
                  <Input value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} maxLength={10} />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Short Description</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={500} />
              </div>

              <div className="space-y-2">
                <Label>Overview</Label>
                <Textarea value={form.overview} onChange={(e) => setForm({ ...form, overview: e.target.value })} maxLength={2000} rows={4} />
              </div>

              <div className="space-y-2">
                <Label>Thumbnail Image</Label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 px-4 py-2 border border-input rounded-md cursor-pointer hover:bg-muted text-sm">
                    <Upload className="w-4 h-4" />
                    {thumbnailFile ? thumbnailFile.name : "Choose file"}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => setThumbnailFile(e.target.files?.[0] || null)} />
                  </label>
                  {editing?.thumbnail_url && !thumbnailFile && (
                    <img src={editing.thumbnail_url} alt="" className="w-16 h-12 object-cover rounded" />
                  )}
                </div>
              </div>

              {/* Section Images: Exterior, Interior, Walkthrough */}
              <div className="border-t border-border pt-6 mt-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Image className="w-5 h-5" /> Project Images
                </h3>

                {imageSections.map((section) => {
                  const sectionExisting = existingImages.filter(
                    (img) => img.section === section && !deletedImageIds.includes(img.id)
                  );
                  const newFiles = sectionFiles[section];

                  return (
                    <div key={section} className="mb-6">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-medium">{sectionLabels[section]}</Label>
                        <label className="flex items-center gap-1.5 px-3 py-1.5 border border-input rounded-md cursor-pointer hover:bg-muted text-xs">
                          <Plus className="w-3 h-3" />
                          Add Images
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={(e) => addSectionFiles(section, e.target.files)}
                          />
                        </label>
                      </div>

                      {(sectionExisting.length > 0 || newFiles.length > 0) ? (
                        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                          {sectionExisting.map((img) => (
                            <div key={img.id} className="relative group">
                              <img src={img.src_url} alt={img.alt || ""} className="w-full h-16 object-cover rounded border border-border" />
                              <button
                                type="button"
                                onClick={() => removeExistingImage(img.id)}
                                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          {newFiles.map((file, i) => (
                            <div key={`new-${i}`} className="relative group">
                              <img src={URL.createObjectURL(file)} alt={file.name} className="w-full h-16 object-cover rounded border border-primary/30" />
                              <button
                                type="button"
                                onClick={() => removeSectionFile(section, i)}
                                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                ×
                              </button>
                              <div className="absolute bottom-0 left-0 right-0 bg-primary/80 text-primary-foreground text-[8px] text-center py-0.5 rounded-b">
                                New
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">No {section} images yet</p>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? "Saving…" : editing ? "Update Project" : "Create Project"}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
              </div>
            </form>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default ProjectsAdmin;
